import React from "react";
import { BatchEventEntity } from "../types";
import { getStatusInfo, formatDateTime, formatPeriod, toPascalCase } from "../batchStatusHelper";
import { CopyableId } from "../../../components/CopyableId";

interface BatchDataGridProps {
  batches: BatchEventEntity[];
  useUtc: boolean;
  onBatchClick: (batch: BatchEventEntity) => void;
}

const headerStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  textAlign: "left" as const,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: "#242424",
  borderBottom: "2px solid #EDEBE9",
  backgroundColor: "#FAFAFA",
};

const cellStyle: React.CSSProperties = {
  padding: "0.625rem 0.75rem",
  fontSize: '0.8125rem',
  color: "#242424",
  borderBottom: "1px solid #F3F2F1",
};

export const BatchDataGrid: React.FC<BatchDataGridProps> = ({ batches, useUtc, onBatchClick }) => {
  return (
    <div style={{ overflowX: "auto" }} tabIndex={0} role="region" aria-label="Batch search results">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={headerStyle}>Batch ID</th>
            <th style={headerStyle}>Status</th>
            <th style={headerStyle}>Country</th>
            <th style={headerStyle}>Company Code</th>
            <th style={headerStyle}>Batch Type</th>
            <th style={{ ...headerStyle, textAlign: "right" }}>Docs</th>
            <th style={{ ...headerStyle, textAlign: "right" }}>Invalid Docs</th>
            <th style={headerStyle}>Period</th>
            <th style={headerStyle}>Created</th>
            <th
              style={{ ...headerStyle, cursor: "help" }}
              title="Estimated send time (report period end or trigger window, plus buffer) shown while the batch is still Staging; becomes the actual buffer-expiry time once the batch starts buffering. Other triggers (e.g. batch size or reporting-period end) can cause the batch to send earlier."
            >
              Scheduled Send <span aria-hidden="true" style={{ color: "#605E5C" }}>&#9432;</span>
            </th>
            <th style={headerStyle}>Sent</th>
            <th style={headerStyle}>Completed</th>
            <th style={{ ...headerStyle, textAlign: "center" }}>Retries</th>
            <th style={headerStyle}>Mode</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => {
            const isBuffering = batch.status.toLowerCase() === "staging" &&
              (batch.bufferExpiryDateTime != null || batch.batchEvents?.some(e => e.message?.includes("Buffering")));
            const statusInfo = getStatusInfo(isBuffering ? "buffering" : batch.status);
            return (
              <tr
                key={batch.id}
                onClick={() => onBatchClick(batch)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBatchClick(batch); } }}
                tabIndex={0}
                role="row"
                aria-label={`Batch ${batch.batchId.substring(0, 8)}, Status ${batch.status}, Country ${batch.countryCode}`}
                style={{ cursor: "pointer", scrollMargin: 8 }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F3F2F1")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                onFocus={(e) => { e.currentTarget.style.backgroundColor = "#F3F2F1"; e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" }); }}
                onBlur={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <td style={{ ...cellStyle, fontFamily: "monospace", color: "#004578", whiteSpace: "nowrap" }}><CopyableId value={batch.batchId} /></td>
                <td style={cellStyle}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: '0.25rem', padding: "0.125rem 0.5rem", borderRadius: '0.75rem', fontSize: '0.75rem', fontWeight: 500, color: statusInfo.color, backgroundColor: statusInfo.bgColor }}>
                    <span aria-hidden="true">{statusInfo.icon}</span> {statusInfo.label}
                  </span>
                </td>
                <td style={cellStyle}>{batch.countryCode}</td>
                <td style={cellStyle}>{batch.companyCode}</td>
                <td style={cellStyle}>{toPascalCase(batch.configType)}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{batch.documentCount.toLocaleString()}</td>
                <td style={{ ...cellStyle, textAlign: "right", color: batch.documentsSkipped > 0 ? "#A4262C" : undefined }}>{batch.documentsSkipped > 0 ? batch.documentsSkipped.toLocaleString() : <span aria-label="None">—</span>}</td>
                <td style={cellStyle}>{formatPeriod(batch.reportPeriodStart, batch.reportPeriodEnd)}</td>
                <td style={{ ...cellStyle, fontSize: '0.75rem' }}>{formatDateTime(batch.createdDateTime, useUtc)}</td>
                <td style={{ ...cellStyle, fontSize: '0.75rem' }} title={batch.bufferExpiryDateTime != null ? "Actual buffer-expiry send time." : "Estimated send time (may change; other triggers like batch size or period end can send earlier)."}>{formatDateTime(batch.bufferExpiryDateTime ?? batch.expectedTriggerDateTime ?? null, useUtc)}{batch.bufferExpiryDateTime == null && batch.expectedTriggerDateTime != null ? <span style={{ color: "#605E5C" }}> (est.)</span> : null}</td>
                <td style={{ ...cellStyle, fontSize: '0.75rem' }}>{formatDateTime(batch.sentDateTime, useUtc)}</td>
                <td style={{ ...cellStyle, fontSize: '0.75rem' }}>{formatDateTime(batch.completedDateTime, useUtc)}</td>
                <td style={{ ...cellStyle, textAlign: "center" }}>{batch.retryCount > 0 ? batch.retryCount : <span aria-label="None">—</span>}</td>
                <td style={cellStyle}>{batch.mode}</td>
              </tr>
            );
          })}
          {batches.length === 0 && (
            <tr>
              <td colSpan={14} style={{ ...cellStyle, textAlign: "center", color: "#A19F9D", padding: 40 }}>No batches found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
